import { motion } from 'framer-motion'

/**
 * Footer component - simplified version
 * Features: Navigation links, background images, copyright
 */
const Footer = () => {
  const navigationItems = [
    { name: 'PRESENTING', href: '#presenting' },
    { name: 'MISSION', href: '#mission' },
    { name: 'FEATURES', href: '#features' },
    { name: 'CONTACT US', href: '#contact' },
  ]

  const handleLinkClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <footer 
      className="relative text-white"
      style={{
        backgroundColor: 'transparent', // Completely transparent to show background images
        marginTop: '0px', // No spacing from contact section
        position: 'relative',
        zIndex: 15 // Footer text visible over background images
      }}
    >
      <div className="relative z-20">
        <div className="container-custom">
          {/* Main Footer Content */}
          <div className="py-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              {/* Logo */}
              <motion.div 
                className="flex items-center justify-center space-x-3 mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <img 
                  src="/logo.png" 
                  alt="KARDS.TECH Logo" 
                  className="h-10 w-auto"
                />
                <div>
                  <div className="text-xl font-bold">
                    <span style={{ color: 'white' }}>KARDS</span>
                    <span style={{ color: '#e5ff47' }}>.TECH</span>
                  </div>
                </div>
              </motion.div>

              {/* Navigation Links */}
              <motion.nav 
                className="flex flex-wrap justify-center gap-4 md:gap-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {navigationItems.map((item, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleLinkClick(item.href)}
                    className="footer-nav-link text-gray-300 hover:text-white transition-colors duration-200 uppercase font-medium tracking-wide"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      letterSpacing: '0.5px'
                    }}
                    whileHover={{ y: -2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {item.name}
                  </motion.button>
                ))}
              </motion.nav>

              {/* Social Media Icons */}
              <motion.div 
                className="flex justify-center items-center mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="twitter"
                  style={{
                    width: '30px',
                    height: '30px',
                    marginRight: '20px',
                    marginLeft: '0',
                    borderRadius: '50%',
                    backgroundColor: '#e5ff47',
                    backgroundImage: 'url(/twitter.svg)',
                    backgroundPosition: '50% 50%',
                    backgroundSize: '15px',
                    backgroundRepeat: 'no-repeat',
                    display: 'inline-block',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  title="Twitter"
                />

                <motion.div
                  className="instagram"
                  style={{
                    width: '30px',
                    height: '30px',
                    marginRight: '20px',
                    marginLeft: '0',
                    borderRadius: '50%',
                    backgroundColor: '#e5ff47',
                    backgroundImage: 'url(/instagram-brands.svg)',
                    backgroundPosition: '50% 50%',
                    backgroundSize: '15px',
                    backgroundRepeat: 'no-repeat',
                    display: 'inline-block',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  title="Instagram"
                />

                <motion.div
                  className="facebook"
                  style={{
                    width: '30px',
                    height: '30px',
                    marginRight: '20px',
                    marginLeft: '0',
                    borderRadius: '50%',
                    backgroundColor: '#e5ff47',
                    backgroundImage: 'url(/facebook-f-brands.svg)',
                    backgroundPosition: '50% 50%',
                    backgroundSize: '15px',
                    backgroundRepeat: 'no-repeat',
                    display: 'inline-block',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  title="Facebook"
                />

                <motion.div
                  className="youtube"
                  style={{
                    width: '30px',
                    height: '30px',
                    marginRight: '0',
                    marginLeft: '0',
                    borderRadius: '50%',
                    backgroundColor: '#e5ff47',
                    backgroundImage: 'url(/youtube-brands.svg)',
                    backgroundPosition: '50% 50%',
                    backgroundSize: '15px',
                    backgroundRepeat: 'no-repeat',
                    display: 'inline-block',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  title="YouTube"
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Copyright */}
          <motion.div 
            className="border-t border-gray-700 py-4 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 text-sm">
              © 2021 Kards.tech . All rights reserved
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
